"use client"

import { useState, useEffect, useRef } from "react"
import { X, Filter } from "lucide-react"
import { format, isSameDay } from "date-fns"
import { ptBR } from "date-fns/locale"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Badge } from "@/components/ui/badge"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const MAX_DATES = 5

type MultiDateFilterProps = {
  onSelect?: (dates: Date[]) => void
  onApply?: (dates: Date[]) => void
  appliedDates?: Date[]
  allowedDates?: Date[]
  defaultDate?: string
}

export default function MultiDateFilter({
  onSelect,
  onApply,
  appliedDates,
  allowedDates,
  defaultDate,
}: MultiDateFilterProps) {
  const [selectedDates, setSelectedDates] = useState<Date[]>(appliedDates || [])
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<"open" | "all">("open")
  const calendarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (appliedDates) {
      setSelectedDates(appliedDates)
    }
  }, [appliedDates])

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return

    const isAlreadySelected = selectedDates.some((selectedDate) => isSameDay(selectedDate, date))

    if (isAlreadySelected) {
      setSelectedDates((prev) => prev.filter((selectedDate) => !isSameDay(selectedDate, date)))
    } else if (selectedDates.length < MAX_DATES) {
      setSelectedDates((prev) => [...prev, date].sort((a, b) => a.getTime() - b.getTime()))
    }
  }

  const removeDate = (dateToRemove: Date) => {
    setSelectedDates((prev) => prev.filter((date) => !isSameDay(date, dateToRemove)))
  }

  const clearAllDates = () => {
    setSelectedDates([])
  }

  useEffect(() => {
    if (onSelect) {
      onSelect(selectedDates)
    }
  }, [selectedDates, onSelect])

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const calendarButton = document.querySelector(".rdp-button")
        if (calendarButton && calendarRef.current && calendarButton instanceof HTMLElement) {
          calendarButton.focus()
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const isDateSelected = (date: Date) => {
    return selectedDates.some((selectedDate) => isSameDay(selectedDate, date))
  }

  const isLimitReached = selectedDates.length >= MAX_DATES

  const isAllowed = (date: Date) => {
    if (activeTab === "all") return true
    if (!allowedDates || allowedDates.length === 0) return true
    return allowedDates.some((allowed) => isSameDay(allowed, date))
  }

  const defaultFocusedDate = defaultDate ? new Date(defaultDate) : undefined

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-9 px-3 justify-start text-left font-normal ${selectedDates.length > 0 ? "border-[#4B303C] bg-blue-50" : ""
            }`}
        >
          <Filter className="mr-2 h-4 w-4" />
          {selectedDates.length === 0 ? (
            "Filtrar por datas"
          ) : (
            <span className="flex items-center gap-1">
              {selectedDates.length} data{selectedDates.length > 1 ? "s" : ""} selecionada
              {selectedDates.length > 1 ? "s" : ""}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 z-50"
        align="start"
        side="bottom"
        sideOffset={4}
        alignOffset={0}
        collisionPadding={16}
        avoidCollisions={true}
        sticky="always"
      >
        <div className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <div className="overflow-y-auto overflow-x-hidden">
            <div className="p-2 xs:p-3 sm:p-4 space-y-2 xs:space-y-3 sm:space-y-4 min-w-0 h-52 xs:h-56 sm:h-64 md:h-72 lg:h-80 xl:h-96 2xl:h-[28rem]">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Filtrar por Datas</h4>
                {selectedDates.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllDates}
                    className="h-6 px-2 text-xs text-red-600 bg-transparent hover:text-red-700 shrink-0"
                  >
                    Limpar
                  </Button>
                )}
              </div>

              <div className="flex border-b items-center justify-center">
                <button
                  onClick={() => setActiveTab("open")}
                  className={cn(
                    "px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors relative flex-1 text-center",
                    activeTab === "open" ? "text-[#4B303C]" : "text-gray-500 hover:text-gray-700",
                  )}
                >
                  <span className="truncate">Datas Abertas</span>
                  {activeTab === "open" && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#4B303C]" />}
                </button>
                <button
                  onClick={() => setActiveTab("all")}
                  className={cn(
                    "px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors relative flex-1 text-center",
                    activeTab === "all" ? "text-[#4B303C]" : "text-gray-500 hover:text-gray-700",
                  )}
                >
                  <span className="truncate">Todas as Datas</span>
                  {activeTab === "all" && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#4B303C]" />}
                </button>
              </div>

              {/* Selected Dates */}
              {selectedDates.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">
                    Selecionadas ({selectedDates.length}/{MAX_DATES}):
                  </p>
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {selectedDates.map((date) => (
                      <Badge
                        key={date.toISOString()}
                        variant="secondary"
                        className="text-xs bg-[#4B303C] text-white hover:bg-[#4B303C]/80 cursor-pointer shrink-0"
                        onClick={() => removeDate(date)}
                      >
                        {format(date, "dd/MM", { locale: ptBR })}
                        <X className="ml-1 h-3 w-3" />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Limit Warning */}
              {isLimitReached && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Limite máximo de {MAX_DATES} datas atingido
                </p>
              )}

              <div ref={calendarRef} className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={undefined}
                  onSelect={handleDateSelect}
                  defaultMonth={defaultFocusedDate}
                  className={cn(
                    "rounded-md border w-full",
                    "text-xs sm:text-sm",
                    "[&_.rdp-months]:w-full",
                    "[&_.rdp-month]:w-full",
                    "[&_.rdp-table]:w-full",
                    "[&_.rdp-cell]:p-0.5 sm:[&_.rdp-cell]:p-1",
                    "[&_.rdp-button]:text-xs sm:[&_.rdp-button]:text-sm",
                  )}
                  modifiers={{
                    selected: (date) => isDateSelected(date),
                    disabled: (date) => (isLimitReached && !isDateSelected(date)) || !isAllowed(date),
                  }}
                  modifiersClassNames={{
                    selected: "bg-[#4B303C] text-white hover:bg-[#4B303C]/80 font-medium",
                    disabled: "opacity-50 cursor-not-allowed",
                  }}
                  locale={ptBR}
                  initialFocus
                />
              </div>
            </div>
          </div>

          <div className="border-t bg-white p-3 sm:p-4 mt-auto shrink-0">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <p className="text-xs text-gray-500 order-2 sm:order-1">Clique nas datas para selecionar</p>
              <Button
                size="sm"
                onClick={() => {
                  if (onApply) {
                    onApply(selectedDates)
                  }
                  setIsOpen(false)
                }}
                className="h-7 px-3 text-xs w-full sm:w-auto order-1 sm:order-2"
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
